<template>
  <!-- The season in one card: your pool points against the app's five and the
       Automatic, then whether going your own way paid, then the weeks (newest
       four, the rest behind one tap). A week opens on Picks. -->
  <section class="card">
    <h2 class="text-[15px] font-extrabold text-white">You vs the app</h2>
    <p class="mt-1 text-[11px] text-slate-400">Pool points. App = its top five by win % at the deadline.</p>

    <div class="mt-3.5 grid gap-1.5" :class="hasBoard ? 'grid-cols-3' : 'grid-cols-2'">
      <div v-for="tile in tiles" :key="tile.key" class="tile" :class="{ 'tile--best': tile.best }">
        <p class="tile-label">{{ tile.label }}</p>
        <p class="tile-points">{{ tile.points }}</p>
        <p class="tile-record">{{ tile.record }}</p>
      </div>
    </div>

    <div v-if="split.picks" class="mt-3 rounded-xl bg-white/[0.03] p-3">
      <p class="label">When you went your own way · {{ split.picks }} pick{{ split.picks === 1 ? "" : "s" }}</p>
      <dl class="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-y-1.5 text-[13px]">
        <dt class="text-slate-200">Your swaps</dt>
        <dd class="font-black tabular-nums" :class="{ 'text-brand-green': swapsWin }">{{ formatRecord(split.swaps) }}</dd>
        <dt class="text-slate-200">The app's picks you skipped</dt>
        <dd class="font-black tabular-nums" :class="{ 'text-brand-green': skippedWin }">{{ formatRecord(split.skipped) }}</dd>
      </dl>
      <p class="mt-2 text-[11px] text-slate-400">{{ netLine }}</p>
    </div>

    <template v-if="rows.length">
      <div class="week-grid mt-3.5 border-b border-white/10 pb-1.5 label" :class="{ 'week-grid--no-app': !hasBoard }">
        <span>Wk</span><span class="text-center">You</span><span v-if="hasBoard" class="text-center">App</span><span class="text-center">Auto</span><span></span>
      </div>
      <router-link
        v-for="row in visibleRows"
        :key="row.week"
        :to="{ path: '/picks', query: { season: String(season), week: String(row.week), season_type: 'regular' } }"
        class="block border-b border-white/[0.07] py-2"
        :aria-label="`Open week ${row.week} on Picks`"
      >
        <span class="week-grid items-center" :class="{ 'week-grid--no-app': !hasBoard }">
          <span class="text-[13px] font-extrabold text-white">{{ row.week }}</span>
          <span v-for="col in columns" :key="col" class="cell" :class="{ 'cell--best': row.best.includes(col), 'cell--empty': !row.records[col] }">
            {{ formatRecord(row.records[col]) }}
          </span>
          <svg class="h-3.5 w-3.5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </span>
        <span v-if="row.note" class="mt-0.5 block pl-10 text-[11px] text-slate-400">{{ row.note }}</span>
      </router-link>
      <button v-if="hiddenWeeks" type="button" class="flex min-h-[44px] w-full items-center justify-between border-b border-white/[0.07] pl-0.5 text-xs font-extrabold text-slate-300" @click="showAll = true">
        <span>Show all {{ rows.length }} weeks</span>
        <svg class="h-3.5 w-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      <p class="mt-2 text-[11px] text-slate-500">Tap a week to open it on Picks.</p>
    </template>
    <p v-else class="py-6 text-center text-xs text-slate-500">No graded weeks yet.</p>
  </section>
</template>

<script>
import { formatRecord, scorecardRows, scorecardTotals, swapNote, swapSplit, withPicks } from "@/utils/seasonScorecard.js";

// Weeks shown before "Show all".
const RECENT_WEEKS = 4;

export default {
  name: "YouVsApp",
  props: {
    // Analysed history games (`analyzeHistoryGame`), the whole season.
    games: { type: Array, default: () => [] },
    // `/history`'s `pool.weeks`.
    weeks: { type: Array, default: () => [] },
    season: { type: [Number, String], required: true },
  },
  data() {
    return { showAll: false };
  },
  computed: {
    ascending() {
      return withPicks(scorecardRows(this.games, this.weeks), this.weeks);
    },
    // Newest first, each with its "Took X over Y" line (none when you matched).
    rows() {
      return [...this.ascending].reverse().map((row) => ({ ...row, note: this.hasBoard ? swapNote(row) : null }));
    },
    visibleRows() {
      return this.showAll ? this.rows : this.rows.slice(0, RECENT_WEEKS);
    },
    hiddenWeeks() {
      return this.rows.length - this.visibleRows.length;
    },
    // A season with no frozen lines (2025) has no app column at all.
    hasBoard() {
      return this.ascending.some((row) => row.hasBoard);
    },
    columns() {
      return this.hasBoard ? ["you", "board", "auto"] : ["you", "auto"];
    },
    totals() {
      return scorecardTotals(this.ascending);
    },
    tiles() {
      const { records, best } = this.totals;
      const tile = (key, label) => ({
        key,
        label,
        points: records[key] ? records[key][0] : "—",
        record: records[key] ? formatRecord(records[key]) : "no graded weeks",
        best: best.includes(key),
      });
      return [tile("you", "You"), ...(this.hasBoard ? [tile("board", "App")] : []), tile("auto", "Auto")];
    },
    split() {
      return this.hasBoard ? swapSplit(this.ascending) : { picks: 0 };
    },
    swapsWin() {
      return this.split.swaps && this.split.skipped && this.split.swaps[0] > this.split.skipped[0];
    },
    skippedWin() {
      return this.split.swaps && this.split.skipped && this.split.skipped[0] > this.split.swaps[0];
    },
    netLine() {
      const { swaps, skipped, shared } = this.split;
      const net = (swaps ? swaps[0] : 0) - (skipped ? skipped[0] : 0);
      const lead = net === 0 ? "Even on your swaps." : `Net ${net > 0 ? "+" : "−"}${Math.abs(net)} for your swaps.`;
      return `${lead} The other ${shared} pick${shared === 1 ? "" : "s"} you shared.`;
    },
  },
  methods: { formatRecord },
};
</script>

<style scoped>
.card { border: 1px solid rgb(255 255 255 / 0.1); border-radius: 1.25rem; background: rgb(1 16 38 / 0.45); padding: 1rem 0.875rem 0.5rem; }
.label { color: #94a3b8; font-size: 9px; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase; }
.tile { border-radius: 0.75rem; background: rgb(255 255 255 / 0.05); padding: 0.625rem 0.625rem 0.55rem; }
.tile--best { background: rgb(16 185 129 / 0.14); box-shadow: inset 0 0 0 1px rgb(52 239 147 / 0.3); }
.tile-label { color: #94a3b8; font-size: 9px; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase; }
.tile--best .tile-label, .tile--best .tile-points { color: #34ef93; }
.tile-points { margin-top: 0.25rem; color: white; font-size: 26px; font-weight: 900; line-height: 28px; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; }
.tile-record { color: #94a3b8; font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; }
.week-grid { display: grid; grid-template-columns: 2.25rem repeat(3, minmax(0, 1fr)) 1.25rem; gap: 0.25rem; }
.week-grid--no-app { grid-template-columns: 2.25rem repeat(2, minmax(0, 1fr)) 1.25rem; }
.cell { border-radius: 0.5rem; padding: 0.3rem 0; text-align: center; color: #e2e8f0; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; }
.cell--best { background: rgb(16 185 129 / 0.15); color: #34ef93; font-weight: 800; }
.cell--empty { color: #475569; }
</style>
